import { z } from "zod";
import { flag, int, lenientArray, optionalText, strings, text } from "./common";

/**
 * Where a pair stands. An unrecognised value reads as `pending` rather than
 * failing: a server that grows a new status must not break one row for
 * every person on an older build.
 */
export const FRIEND_STATUSES = ["pending", "accepted", "blocked"] as const;
export type FriendStatus = (typeof FRIEND_STATUSES)[number];
export const friendStatusSchema = z.enum(FRIEND_STATUSES).catch("pending");

/** One person in the graph. */
export const friendSchema = z.object({
  user_id: optionalText(),
  username: optionalText(),
  status: friendStatusSchema,
  outgoing: flag(),
  mutuals: int(),
});
export type Friend = Readonly<z.output<typeof friendSchema>>;

export const friendListSchema = z.object({ friends: lenientArray(friendSchema) });

const contactMatchSchema = z.object({
  user_id: text(),
  username: optionalText(),
  hash: optionalText(),
});
export type ContactMatch = Readonly<z.output<typeof contactMatchSchema>>;

/** `POST /me/contacts`: which hashes belong to people already here. */
export const contactMatchResultSchema = z.object({
  uploaded: int(),
  matches: lenientArray(contactMatchSchema),
});
export type ContactMatchResult = Readonly<z.output<typeof contactMatchResultSchema>>;

/** The signed-in person's handle and public-page setting. */
export const socialProfileSchema = z.object({
  user_id: text(),
  username: optionalText(),
  profile_public: flag(),
  has_phone: flag(),
});
export type SocialProfile = Readonly<z.output<typeof socialProfileSchema>>;

/** `GET /me/compatibility/{id}`: two charts read together. */
export const compatibilityReadingSchema = z.object({
  headline: text(),
  runs_easily: text(),
  takes_work: text(),
  today: text(),
  why: strings(),
});
export type CompatibilityReading = Readonly<z.output<typeof compatibilityReadingSchema>>;

/** The reasons the backend accepts for a report. Must match `REPORT_REASONS`. */
export const REPORT_REASONS = [
  "harassment",
  "spam",
  "impersonation",
  "inappropriate",
  "other",
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_REASON_LABEL: Readonly<Record<ReportReason, string>> = {
  harassment: "Harassment or bullying",
  spam: "Spam",
  impersonation: "Pretending to be someone else",
  inappropriate: "Inappropriate content",
  other: "Something else",
};
